'use client';

import { useEffect } from 'react';

import { withSentry } from '~/lib/sentry-client';

const THIRD_PARTY_DOMAINS = [
  'googleadservices.com',
  'googletagmanager.com',
  'googlesyndication.com',
  'doubleclick.net',
  'google-analytics.com',
  'facebook.net',
  'connect.facebook.com',
];

// DOM exceptions raised inside React's commit phase when an in-browser
// translator has re-parented text nodes. The translator-dom-safeguard
// polyfill (instrumentation-client.ts) neutralises these at runtime, so
// any that still slip through are untriageable noise.
const TRANSLATOR_DOM_ERROR_MESSAGES = [
  'The object can not be found here',
  'Failed to execute \'removeChild\' on \'Node\'',
  'Failed to execute \'insertBefore\' on \'Node\'',
];

function isTranslatorDomError(
  message: string,
  frames: Array<{ filename?: string }>,
): boolean {
  if (!TRANSLATOR_DOM_ERROR_MESSAGES.some((m) => message.includes(m))) return false;
  return frames.some((f) => f.filename?.includes('react-dom'));
}

function hasOnlyOpaqueFrames(frames: Array<{ filename?: string }>): boolean {
  if (frames.length === 0) return false;
  return frames.every(
    (f) => !f.filename || f.filename === 'undefined' || f.filename === '<anonymous>',
  );
}

function hasMeasurementConsent(): boolean {
  if (typeof document === 'undefined') return false;

  const cookie = document.cookie.split('; ').find((c) => c.startsWith('c15t-consent='));

  if (!cookie) return false;

  const value = cookie.substring('c15t-consent='.length);

  return value.split(',').some((s) => {
    const [key, val] = s.split(':');
    return key === 'c.measurement' && val === '1';
  });
}

function initSentry() {
  withSentry((Sentry) => {
    Sentry.init({
      dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
      // Release must match the identifier used by scripts/upload-sourcemaps.mjs
      // so uploaded source maps associate with events.
      release: process.env.NEXT_PUBLIC_GIT_COMMIT_SHA || undefined,
      tracesSampleRate: 0,
      enabled: process.env.NODE_ENV === 'production',
      beforeSend(event) {
        if (!hasMeasurementConsent()) return null;

        const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
        const errorMessage = event.exception?.values?.[0]?.value ?? '';

        const isThirdParty = THIRD_PARTY_DOMAINS.some(
          (domain) =>
            frames.some((f) => f.filename?.includes(domain)) ||
            errorMessage.includes(domain),
        );
        if (isThirdParty) return null;

        if (isTranslatorDomError(errorMessage, frames)) return null;

        // Errors whose every frame is anonymous/`undefined:` come from inline
        // snippets or extension-injected scripts (e.g. the RangeError the
        // translator shim triggers). They are not actionable in our code.
        if (hasOnlyOpaqueFrames(frames)) return null;

        return event;
      },
    });
  });
}

export function LazySentry() {
  useEffect(() => {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(initSentry);
    } else {
      setTimeout(initSentry, 2000);
    }
  }, []);

  return null;
}
